/**
 * @fileoverview Button Controller - ボタン状態管理とDOM更新
 */

import { GameStatus } from '../../core/constants.js';
import { STATE_PATHS } from '../../shared/constants/state-paths.js';

import { onPathChange } from './subscription-utils.js';

const ARIA_DISABLED = 'aria-disabled';
const ARIA_HIDDEN = 'aria-hidden';
const ARIA_PRESSED = 'aria-pressed';
const CSS_CLASS_IS_DISABLED = 'is-disabled';
const CSS_CLASS_IS_LOADING = 'is-loading';

/**
 * 要素の属性を一括設定する汎用関数
 */
function setElementState(element, attributes) {
  Object.entries(attributes).forEach(([key, value]) => {
    if (key === 'disabled') {
      if (value) {
        element.setAttribute('disabled', '');
      } else {
        element.removeAttribute('disabled');
      }
    } else {
      element.setAttribute(key, String(value));
    }
  });
}

/**
 * 全ボタン共通の基本状態を適用
 * ARIA属性とdisabled属性の設定のみを担当
 * CSSクラス制御は各ボタンのapply関数に委譲
 */
function applyCommonButtonState(element, state) {
  // ARIA属性とdisabled属性の設定
  setElementState(element, {
    disabled: !state.enabled,
    [ARIA_DISABLED]: String(!state.enabled),
    [ARIA_PRESSED]: String(state.pressed),
    [ARIA_HIDDEN]: String(!state.visible),
  });

  // disabled属性（form要素互換性のため）
  const isDomElement =
    typeof element === 'object' &&
    element !== null &&
    (typeof element.ownerDocument !== 'undefined' || element.nodeType === 1);

  if (isDomElement) {
    element.disabled = !state.enabled;
  } else if ('disabled' in element) {
    element.disabled = Boolean(state.enabled);
  }
}

const BUTTON_CONFIGS = {
  start: {
    elementId: 'button',
    calculate(gameState) {
      const status = gameState.getState(STATE_PATHS.CORE_STATUS);

      const flags = {
        isTitle: status === GameStatus.Title,
        isReady: status === GameStatus.Ready,
        isProcessing: status === GameStatus.Processing,
        isPaused: status === GameStatus.Paused,
        isGameOver: status === GameStatus.GameOver,
      };

      const state = {
        // Titleでのみ有効
        enabled: flags.isTitle,
        visible: true,
        // Title以外は押し込み状態を表示
        pressed: !flags.isTitle,
        loading: flags.isProcessing,
      };

      return { state, flags };
    },
    apply(element, { state, flags }) {
      // 共通CSSクラス制御（applyCommonButtonStateから移譲）
      element.classList.toggle(CSS_CLASS_IS_DISABLED, !state.enabled);
      element.classList.toggle(CSS_CLASS_IS_LOADING, state.loading);
      element.classList.toggle('clicked', state.pressed);
      element.style.pointerEvents = state.enabled ? 'auto' : 'none';

      // キャプション制御のためのCSSクラス管理
      const shouldShowPlayingState =
        flags.isReady || flags.isProcessing || flags.isPaused || flags.isGameOver;
      element.classList.toggle('is-play-disabled', shouldShowPlayingState);

      // is-paused: Paused状態のみ（"Paused"キャプション表示用）
      element.classList.toggle('is-paused', flags.isPaused);
    },
  },
  skill: {
    elementId: 'skill-button',
    calculate(gameState) {
      const status = gameState.getState(STATE_PATHS.CORE_STATUS);
      const uiState = gameState.getState(STATE_PATHS.UI);

      const flags = {
        isReady: status === GameStatus.Ready,
        isGameOver: status === GameStatus.GameOver,
        isTargeting: uiState.isSkillTargeting,
        hasAmmo: uiState.remainingSkillUses > 0,
      };

      const state = {
        // Ready かつ 弾薬あり かつ GameOver中でない
        enabled: flags.isReady && flags.hasAmmo && !flags.isGameOver,
        visible: true,
        pressed: flags.isTargeting,
        loading: false,
      };

      return { state, flags };
    },
    apply(element, { state }, gameState) {
      const uiState = gameState.getState(STATE_PATHS.UI);

      // 共通CSSクラス制御（applyCommonButtonStateから移譲）
      element.classList.toggle(CSS_CLASS_IS_DISABLED, !state.enabled);
      element.classList.toggle(CSS_CLASS_IS_LOADING, state.loading);
      element.classList.toggle('clicked', state.pressed);
      element.style.pointerEvents = state.enabled ? 'auto' : 'none';

      // スキルボタン特有の処理
      element.classList.toggle('is-armed', uiState.isSkillTargeting);

      // ターゲティング中の視覚的フィードバック
      element.classList.toggle('is-targeting', uiState.isSkillTargeting);
    },
  },
  menu: {
    elementId: 'menu-button',
    calculate(gameState) {
      const status = gameState.getState(STATE_PATHS.CORE_STATUS);
      const uiState = gameState.getState(STATE_PATHS.UI);

      // GameState から取得（DOM 依存を削除）
      const isDrawerOpen = gameState.getState(STATE_PATHS.UI_IS_DRAWER_OPEN);

      const flags = {
        isTitle: status === GameStatus.Title,
        isReady: status === GameStatus.Ready,
        isGameOver: status === GameStatus.GameOver,
        isDrawerOpen,
      };

      const state = {
        // canOpenMenu または ドロワー開閉中
        enabled:
          ((flags.isTitle || flags.isReady) && !uiState.isSkillTargeting && !flags.isGameOver) ||
          flags.isDrawerOpen,
        visible: true,
        pressed: flags.isDrawerOpen, // ドロワーが開いている間は押し込み状態を維持
        loading: false,
      };

      return { state, flags };
    },
    apply(element, { state, flags }) {
      // 共通CSSクラス制御（applyCommonButtonStateから移譲）
      element.classList.toggle(CSS_CLASS_IS_DISABLED, !state.enabled);
      element.classList.toggle(CSS_CLASS_IS_LOADING, state.loading);
      element.classList.toggle('clicked', state.pressed);

      // メニューボタン特有のpointerEvents制御
      // ドロワーが開いている間はクリックを無効化（視覚的には押し込み状態を維持）
      if (!state.enabled) {
        element.style.pointerEvents = 'none';
      } else {
        element.style.pointerEvents = flags.isDrawerOpen ? 'none' : 'auto';
      }

      // メニューボタン特有の処理
      // ドロワーが開いている時の視覚的状態
      element.classList.toggle('is-drawer-open', flags.isDrawerOpen);

      // transformプロパティはCSSの!importantルールに完全に委ねる
      // JavaScriptでの変更は一切行わない
      element.style.transform = '';
    },
  },
};

function createButtonUpdater(key) {
  const config = BUTTON_CONFIGS[key];

  return function update(gameState) {
    const element = document.getElementById(config.elementId);
    if (!element) return;

    const computed = config.calculate(gameState);

    // 共通のボタン状態を適用
    applyCommonButtonState(element, computed.state);

    if (typeof config.apply === 'function') {
      config.apply(element, computed, gameState);
    }
  };
}

/**
 * スタートボタンの状態を計算
 * 純粋関数: GameStateを読み取り専用で使用
 */
export function calculateStartButtonState(gameState) {
  return BUTTON_CONFIGS.start.calculate(gameState);
}

/**
 * スキルボタンの状態を計算
 * 純粋関数: GameStateを読み取り専用で使用
 */
export function calculateSkillButtonState(gameState) {
  return BUTTON_CONFIGS.skill.calculate(gameState);
}

/**
 * メニューボタンの状態を計算
 * 純粋関数: GameStateを読み取り専用で使用
 */
export function calculateMenuButtonState(gameState) {
  return BUTTON_CONFIGS.menu.calculate(gameState);
}

export const updateStartButton = createButtonUpdater('start');
export const updateSkillButton = createButtonUpdater('skill');
export const updateMenuButton = createButtonUpdater('menu');

/**
 * 全ボタンの一括更新
 * 純粋関数: 各ボタン更新関数を呼び出すだけ
 */
export function updateAllButtons(gameState) {
  updateStartButton(gameState);
  updateSkillButton(gameState);
  updateMenuButton(gameState);
  // AnimatedText is now handled by animation-coordinator.js

  // All buttons updated - no logging needed
}

// AnimatedText (MATCH callout) is now handled by animation-coordinator.js
// This eliminates the need for debouncing since core.status changes are atomic

/**
 * ボタンコントローラーの初期化
 * GameStateの変更を監視してボタン表示を自動更新
 */
export function initializeButtonController(gameState) {
  // ボタン表示に影響する状態変更を監視（境界バグ回避版）
  // UI_IS_SKILL_TARGETING は削除（スキル操作時に直接 updateAllButtons を呼ぶ）
  return onPathChange(
    gameState,
    [STATE_PATHS.CORE_STATUS, STATE_PATHS.UI_REMAINING_SKILL_USES, STATE_PATHS.UI_IS_DRAWER_OPEN],
    () => {
      updateAllButtons(gameState);
    },
    true,
  );
}
